import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { HouseSimple, MagnifyingGlass, GearSix, Terminal, Users, Export, SignOut, List, X, Bell, BookmarkSimple, CurrencyDollar, Buildings, UserCircle, IdentificationCard } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotificationsBell } from "@/components/NotificationsBell";
import { CompanyLogo } from "@/components/CompanyLogo";

const NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: HouseSimple, roles: "all" },
  { path: "/buscar", label: "Buscador", icon: MagnifyingGlass, roles: "all" },
  { path: "/favoritos", label: "Favoritos", icon: BookmarkSimple, roles: "all" },
  { path: "/alertas", label: "Alertas", icon: Bell, roles: "all" },
  { path: "/proveedores", label: "Proveedores", icon: IdentificationCard, roles: "all" },
  { path: "/scraper/config", label: "Configuración", icon: GearSix, roles: "system_admin,editor" },
  { path: "/scraper/monitor", label: "Monitor", icon: Terminal, roles: "system_admin,editor,tech_support" },
  { path: "/usuarios", label: "Usuarios", icon: Users, roles: "system_admin,client_admin" },
  { path: "/admin/clientes", label: "Clientes", icon: Buildings, roles: "system_admin" },
  { path: "/exportar", label: "Exportar", icon: Export, roles: "all" },
  { path: "/perfil", label: "Mi Cuenta", icon: UserCircle, roles: "all" },
  { path: "/precios", label: "Precios", icon: CurrencyDollar, roles: "all" },
];

const ROLE_LABELS = {
  system_admin: "Admin Sistema",
  editor: "Editor",
  tech_support: "Soporte",
  client_admin: "Admin Cliente",
  standard_user: "Usuario",
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/account/me").then((r) => setAccount(r.data.account)).catch(() => {});
  }, [user]);

  const visibleItems = NAV_ITEMS.filter(
    (item) => item.roles === "all" || item.roles.split(",").includes(user?.role)
  );

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-border/40">
        <h1 className="font-heading font-black text-xl tracking-tight" data-testid="app-logo">
          LICITA<span className="text-[#002FA7]">AI</span>
        </h1>
        <p className="text-xs text-muted-foreground mt-1 font-mono">Inteligencia en licitaciones</p>
        {account && (
          <div className="mt-4 flex items-center gap-2 pt-3 border-t border-border/40" data-testid="account-badge">
            <CompanyLogo logoUrl={account.logo_url} companyName={account.company_name} size={28} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{account.company_name}</p>
              <p className="text-[10px] font-mono text-muted-foreground uppercase">{account.plan_id}</p>
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 py-3 px-2" data-testid="sidebar-nav">
        {visibleItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.path === "/" ? "dashboard" : item.path.replace(/\//g, "-").replace(/^-/, "")}`}
              onClick={() => { navigate(item.path); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-none transition-colors duration-200 mb-px ${
                isActive
                  ? "bg-[#002FA7] text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              <item.icon size={20} weight={isActive ? "fill" : "regular"} />
              {item.label}
            </button>
          );
        })}
      </nav>

      <Separator />
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold rounded-none">
            {user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</p>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 rounded-none font-mono" data-testid="user-role">
              {ROLE_LABELS[user?.role] || user?.role}
            </Badge>
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="mt-3 w-full flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <SignOut size={16} weight="regular" />
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-64 border-r border-border/40 bg-card flex-col flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card border-r border-border/40 flex flex-col">
            <div className="absolute top-4 right-[-40px]">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} data-testid="close-sidebar">
                <X size={20} />
              </Button>
            </div>
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Desktop header */}
        <header className="hidden lg:flex items-center justify-end gap-2 px-6 py-3 border-b border-border/40 bg-card">
          <NotificationsBell />
        </header>

        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-border/40 bg-card">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar">
            <List size={24} />
          </Button>
          <h1 className="font-heading font-black text-lg">LICITA<span className="text-[#002FA7]">AI</span></h1>
          <NotificationsBell />
        </header>

        <ScrollArea className="flex-1">
          <main className="p-6 lg:p-8">
            {children}
          </main>
        </ScrollArea>
      </div>
    </div>
  );
};
